import React, { memo, useCallback, useMemo, useRef } from "react";
import {
  Box,
  HStack,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
} from "@chakra-ui/react";

import { useAuth } from "modules/providers/auth-provider";
import UserAvatar from "modules/_shared/user-avatar";
import colors from "app/colors";
import {
  MessageFullySeenIcon,
  EyeSlashIcon,
  MessagePartiallySeenIcon,
  CheckIcon,
} from "modules/icons";
import UserName from "modules/_shared/user-name";

import {
  MIXPANEL_EVENT_TYPE,
  useMixpanel,
} from "modules/providers/mixpanel-provider/mixpanel-provider";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import { useUsers } from "modules/providers/users-provider";
import { MessageThreadFragment, MessageFragment } from "gql/graphql";
import { useOverlay } from "modules/providers/overlay-provider";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
}

const MessageSeen: React.FC<IProps> = ({ messageThread, message }) => {
  const { me } = useAuth();
  const { usersById } = useUsers();
  const { openDrawer } = useOverlay();
  const { track } = useMixpanel();
  const openMessageInfoRef = useRef<HTMLButtonElement>(null);

  // need to combine users who have already seen it with those who have access
  // if a user was removed from the thread after seeing the message, they should still show up in the seen list
  const filteredAccessUserIdList = useMemo(() => {
    const isValidUser = (userId: string) =>
      userId !== message.createdUserId && usersById[userId]?.active;

    return Array.from(
      new Set([...messageThread.accessUserIdList, ...message.seenUserIdList]),
    ).filter(isValidUser);
  }, [
    message.createdUserId,
    message.seenUserIdList,
    messageThread.accessUserIdList,
    usersById,
  ]);

  const filteredSeenUserIdList = useMemo(
    () =>
      message.seenUserIdList.filter(
        (userId) =>
          userId !== message.createdUserId && usersById[userId]?.active,
      ),
    [message.createdUserId, message.seenUserIdList, usersById],
  );

  const seenIcon = useCallback(
    (colorOverride?: string) => {
      if (filteredSeenUserIdList.length === 0) {
        return (
          <EyeSlashIcon boxSize={4} fill={colorOverride ?? colors.gray30} />
        );
      }

      if (filteredSeenUserIdList.length < filteredAccessUserIdList.length) {
        return (
          <MessagePartiallySeenIcon
            boxSize={4}
            fill={colorOverride ?? colors.gray50}
          />
        );
      }

      return (
        <HStack spacing={0}>
          <CheckIcon
            boxSize={4}
            fill={colorOverride ?? colors.greenPrimary}
            mr={-0.5}
          />
          <MessageFullySeenIcon
            boxSize={4}
            fill={colorOverride ?? colors.greenPrimary}
          />
        </HStack>
      );
    },
    [filteredAccessUserIdList.length, filteredSeenUserIdList.length],
  );

  const openMessageProfile = useCallback(() => {
    openMessageInfoRef.current?.blur(); // prevents focus when returning, even if the seen icon button is no longer being hovered
    openDrawer({
      type: DRAWER_TYPE.MESSAGE_PROFILE,
      id: message.id,
      id2: messageThread.id,
    });
    track(MIXPANEL_EVENT_TYPE.MESSAGE_DETAILS_VIEWED);
  }, [openDrawer, message.id, messageThread.id, track]);

  return (
    <HStack spacing={0.5}>
      {filteredAccessUserIdList.length <= 5 && (
        <HStack spacing={-1}>
          {filteredSeenUserIdList.map((userId, index) => (
            <Box
              key={userId}
              h={5}
              borderWidth={2}
              rounded="full"
              zIndex={index + 1}
              borderColor={
                message.createdUserId === me?.id
                  ? colors.messageBackgroundSelf
                  : colors.messageBackground
              }
            >
              <UserAvatar
                userId={userId}
                size="2xs"
                displayOnlineIndicator={false}
              />
            </Box>
          ))}
        </HStack>
      )}
      {filteredAccessUserIdList.length > 5 &&
        filteredSeenUserIdList.length > 0 && (
          <Text fontSize={11} color={colors.gray50}>
            {filteredSeenUserIdList.length}/{filteredAccessUserIdList.length}
          </Text>
        )}
      <Popover placement="bottom-end" trigger="hover" isLazy>
        <PopoverTrigger>
          <CircularIconButton
            ref={openMessageInfoRef}
            aria-label="Open message info"
            icon={seenIcon()}
            onClick={openMessageProfile}
            minW={4}
            h={4}
          />
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            w="fit-content"
            bg={colors.blackSecondary}
            color="white"
            p={3}
          >
            <PopoverArrow bg={colors.blackSecondary} />
            <Stack>
              {filteredSeenUserIdList.length === 0 && (
                <Text fontSize="sm">Not yet seen</Text>
              )}
              {filteredSeenUserIdList.length > 0 && (
                <HStack>
                  {seenIcon("white")}
                  <Text fontSize="sm">
                    {filteredSeenUserIdList.length <
                    filteredAccessUserIdList.length
                      ? `Seen by ${filteredSeenUserIdList.length}/${filteredAccessUserIdList.length}`
                      : `Seen by Everyone ${filteredSeenUserIdList.length}/${filteredAccessUserIdList.length}`}
                  </Text>
                </HStack>
              )}
              {filteredSeenUserIdList.length > 0 && (
                <Stack
                  overflowY="auto"
                  maxH="23.5vh" // shows enough items w/ the last item being half visible to make it obvious there is more to see
                >
                  {filteredSeenUserIdList.map((userId) => (
                    <HStack key={userId}>
                      <UserAvatar
                        userId={userId}
                        size="2xs"
                        displayOnlineIndicator={false}
                      />
                      <Text fontSize={11}>
                        <UserName userId={userId} />
                      </Text>
                    </HStack>
                  ))}
                </Stack>
              )}
            </Stack>
          </PopoverContent>
        </Portal>
      </Popover>
    </HStack>
  );
};

export default memo(MessageSeen);
